import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, unwrapObject } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Boxes, CheckCircle2, FileCheck2, FileText, Loader2, MapPin, MessageSquareWarning, Package, Pencil, Printer, ReceiptText, Truck, Undo2, XCircle } from "lucide-react";
import { formatINR } from "@/lib/utils";
import type { DispatchRecord, OrderLineRecord, OrderRecord, OrderStatus } from "@/types";
import { DispatchStatusBadge, OrderStatusBadge } from "@/components/orders/OrderStatusBadge";
import { DispatchDialog } from "@/components/orders/DispatchDialog";
import { DispatchPaperwork, EwayDialog, PodDialog, TrackingDialog, onTheRoad, uploadPod } from "@/components/orders/DispatchExtras";
import { AmendLinesDialog } from "@/components/orders/AmendLinesDialog";
import { ProformaDialog } from "@/components/orders/ProformaDialog";
import { ComplaintFormDialog } from "@/components/complaints/ComplaintFormDialog";
import { ReturnDialog } from "@/components/orders/ReturnDialog";
import { StockPickerDialog } from "@/components/inventory/StockPickerDialog";
import { OrderProductionCard } from "@/components/production/OrderProductionCard";

const formatDate = (d?: string | null) => (d ? new Date(d.length === 10 ? `${d}T00:00:00` : d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-");
const num = (v: any) => Number(v || 0);
const DELIVERY_TERMS: Record<string, string> = { EX_MILL: "Ex-mill / Ex-works", FOR: "FOR destination", FOB: "FOB", CIF: "CIF", DOOR: "Door delivery" };

type Action = { kind: "deliver" | "cancelDispatch" | "cancelOrder"; dispatch?: DispatchRecord } | null;

export function OrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { success, error } = useToast();
  const [order, setOrder] = useState<OrderRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [dispatchOpen, setDispatchOpen] = useState(false);
  const [amendOpen, setAmendOpen] = useState(false);
  const [proformaOpen, setProformaOpen] = useState(false);
  const [complaintOpen, setComplaintOpen] = useState(false);
  const [reserveLine, setReserveLine] = useState<OrderLineRecord | null>(null);
  const [returnFor, setReturnFor] = useState<DispatchRecord | null>(null);
  const [trackFor, setTrackFor] = useState<DispatchRecord | null>(null);
  const [ewayFor, setEwayFor] = useState<DispatchRecord | null>(null);
  const [podFor, setPodFor] = useState<DispatchRecord | null>(null);
  const [receivedBy, setReceivedBy] = useState("");
  const [podFile, setPodFile] = useState<File | null>(null);
  const [action, setAction] = useState<Action>(null);
  const [text, setText] = useState("");
  const [date, setDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [edit, setEdit] = useState({ po_number: "", po_date: "", delivery_terms: "", payment_terms: "", notes: "" });
  const canUpdate = hasPermission("order.update");
  const canComplain = hasPermission("complaint.create");

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setOrder(unwrapObject<OrderRecord>(await api.get(`/api/SalesOrder/ById/${id}`)));
    } catch (err: any) {
      error("Failed to load order", err.message || "Order not found");
      navigate("/orders");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading || !order) return <Loader2 className="size-5 animate-spin text-muted-foreground m-6" />;
  const cancelled = order.order_status === "CANCELLED";
  const hasPending = (order.lines || []).some((l) => num(l.max_dispatch_qty) > 0);
  const liveDispatches = (order.dispatches || []).filter((d) => d.dispatch_status !== "CANCELLED");
  const lineName = (lineId: string) => (order.lines || []).find((l: OrderLineRecord) => l.id === lineId);

  const openEdit = () => {
    setEdit({ po_number: order.po_number, po_date: order.po_date ? String(order.po_date).slice(0, 10) : "", delivery_terms: order.delivery_terms || "", payment_terms: order.payment_terms || "", notes: order.notes || "" });
    setEditOpen(true);
  };

  const run = async (fn: () => Promise<any>, ok: string) => {
    setBusy(true);
    try {
      await fn();
      success(ok, "");
      setAction(null);
      setEditOpen(false);
      await load();
    } catch (err: any) {
      error("Could not complete that", err.message || "Please try again");
    } finally {
      setBusy(false);
    }
  };

  const openAction = (a: NonNullable<Action>) => {
    setText("");
    setReceivedBy("");
    setPodFile(null);
    setDate(new Date().toISOString().slice(0, 10));
    setAction(a);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="space-y-1">
          <button type="button" onClick={() => navigate("/orders")} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
            <ArrowLeft className="size-3.5" /> Orders
          </button>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2.5 flex-wrap">
            {order.order_number} <OrderStatusBadge status={order.order_status as OrderStatus} />
          </h1>
          <p className="text-sm text-muted-foreground">
            {order.account?.name} ·{" "}
            <Link to={`/deals/${order.deal_id}`} className="text-primary hover:underline">
              {order.deal?.name}
            </Link>
            {order.quote_number ? ` · from quote ${order.quote_number}` : ""}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={() => window.open(`/api/SalesOrder/Pdf/${order.id}`, "_blank")}>
            <Printer className="size-4" /> Order confirmation
          </Button>
          {(order.proforma_number || (canUpdate && !cancelled)) && (
            <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={() => setProformaOpen(true)}>
              <FileText className="size-4" /> {order.proforma_number ? `Proforma ${order.proforma_number}` : "Proforma invoice"}
            </Button>
          )}
          {canComplain && !cancelled && (
            <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={() => setComplaintOpen(true)}>
              <MessageSquareWarning className="size-4" /> Log complaint
            </Button>
          )}
          {canUpdate && !cancelled && (
            <>
              <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={openEdit}>
                <Pencil className="size-4" /> Edit
              </Button>
              {liveDispatches.length === 0 && (
                <Button variant="outline" size="sm" className="h-9 gap-1.5 text-red-600" onClick={() => openAction({ kind: "cancelOrder" })}>
                  <XCircle className="size-4" /> Cancel order
                </Button>
              )}
              {hasPending && (
                <Button size="sm" className="h-9 gap-1.5 shadow-sm" onClick={() => setDispatchOpen(true)}>
                  <Truck className="size-4" /> Dispatch
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {cancelled && order.cancelled_reason && (
        <div className="rounded-xl border bg-muted/40 px-4 py-3 text-sm">
          <strong>Cancelled:</strong> {order.cancelled_reason}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Info label="Buyer's PO" value={`${order.po_number}${order.po_date ? ` · ${formatDate(order.po_date)}` : ""}`} />
        <Info label="Delivery terms" value={order.delivery_terms ? DELIVERY_TERMS[order.delivery_terms] || order.delivery_terms : "-"} />
        <Info label="Payment terms" value={order.payment_terms || "-"} />
        <Info label="Order value" value={`${formatINR(order.taxable_total)} + GST ${formatINR(order.tax_total)} = ${formatINR(order.grand_total)}`} />
      </div>

      <div className="bg-card border rounded-2xl overflow-hidden">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h3 className="text-sm font-bold">Lines</h3>
          {canUpdate && !cancelled && (
            <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => setAmendOpen(true)}>
              <Pencil className="size-3.5" /> Amend lines
            </Button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 text-muted-foreground">
              <tr>
                <th className="text-left font-semibold px-3 py-2 min-w-[220px]">Item</th>
                <th className="text-right font-semibold px-2 py-2">Ordered</th>
                <th className="text-right font-semibold px-2 py-2">Sent</th>
                <th className="text-right font-semibold px-2 py-2">Pending</th>
                <th className="text-left font-semibold px-2 py-2 w-32">Progress</th>
                <th className="text-right font-semibold px-2 py-2">Rate (₹)</th>
                <th className="text-right font-semibold px-3 py-2">Amount (₹)</th>
              </tr>
            </thead>
            <tbody>
              {(order.lines || []).map((l) => {
                const pct = num(l.quantity) > 0 ? Math.min(100, Math.round((num(l.dispatched_qty) / num(l.quantity)) * 100)) : 0;
                return (
                  <tr key={l.id} className="border-t align-top">
                    <td className="px-3 py-2">
                      <div className="font-medium text-sm">{l.product_name}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {l.product_code ? <code className="font-mono">{l.product_code}</code> : null}
                        {l.variant_name ? ` · Shade: ${l.variant_name}` : ""}
                      </div>
                      {l.spec_snapshot?.specs?.length ? <div className="text-[11px] text-muted-foreground">{l.spec_snapshot.specs.map((s) => `${s.label}: ${s.value}`).join(" | ")}</div> : null}
                      <div className="text-[11px] text-muted-foreground">
                        {[l.tolerance_pct ? `Tolerance ±${num(l.tolerance_pct)}%` : null, l.packing ? `Packing: ${l.packing}` : null, l.delivery_date ? `Delivery by ${formatDate(l.delivery_date)}` : null].filter(Boolean).join(" · ")}
                      </div>
                      {(num(l.reserved_units) > 0 || (canUpdate && !cancelled && num(l.pending_qty) > 0 && l.product_id)) && (
                        <div className="mt-1 flex items-center gap-2 flex-wrap">
                          {num(l.reserved_units) > 0 && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-400 px-2 py-0.5 text-[11px] font-medium">
                              <Boxes className="size-3" /> {num(l.reserved_units)} roll{num(l.reserved_units) === 1 ? "" : "s"} held · {num(l.reserved_qty)} {l.unit}
                            </span>
                          )}
                          {canUpdate && !cancelled && num(l.pending_qty) > 0 && l.product_id && (
                            <button type="button" className="text-[11px] text-primary hover:underline" onClick={() => setReserveLine(l)}>
                              {num(l.reserved_units) > 0 ? "Change held stock" : "Hold stock"}
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right">{num(l.quantity)} {l.unit}</td>
                    <td className="px-2 py-2 text-right">{num(l.dispatched_qty)}</td>
                    <td className="px-2 py-2 text-right font-medium">{num(l.pending_qty)}</td>
                    <td className="px-2 py-2">
                      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                        <div className={`h-full ${l.complete ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${pct}%` }} />
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-1">{l.complete ? "Complete" : `${pct}%`}</div>
                    </td>
                    <td className="px-2 py-2 text-right">{num(l.unit_price).toLocaleString("en-IN")}</td>
                    <td className="px-3 py-2 text-right font-medium">{num(l.taxable_amount).toLocaleString("en-IN")}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {hasPermission("production.read") && <OrderProductionCard orderId={order.id} orderNumber={order.order_number} lines={order.lines || []} cancelled={cancelled} />}

      <div className="bg-card border rounded-2xl overflow-hidden">
        <div className="px-4 py-3 border-b">
          <h3 className="text-sm font-bold">Dispatches</h3>
        </div>
        {(order.dispatches || []).length === 0 ? (
          <p className="px-4 py-6 text-sm text-muted-foreground">Nothing has been sent yet.</p>
        ) : (
          <ul className="divide-y">
            {(order.dispatches || []).map((d) => (
              <li key={d.id} className="px-4 py-3 space-y-1.5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-sm font-semibold flex items-center gap-2">
                      <Truck className="size-4 text-muted-foreground" /> {d.dispatch_number} <DispatchStatusBadge status={d.dispatch_status} />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(d.dispatch_date)} · {d.transporter}
                      {d.lr_number ? ` · LR ${d.lr_number}` : ""}
                      {d.vehicle_number ? ` · ${d.vehicle_number}` : ""}
                      {d.packages ? ` · ${d.packages} rolls/bales` : ""}
                      {d.invoice_number ? ` · invoice ${d.invoice_number}` : ""}
                    </p>
                    {d.delivered_on && <p className="text-xs text-emerald-700 dark:text-emerald-400">Delivered on {formatDate(d.delivered_on)}</p>}
                    <DispatchPaperwork d={d} />
                    {d.cancelled_reason && <p className="text-xs text-muted-foreground">Cancelled: {d.cancelled_reason}</p>}
                  </div>
                  <div className="flex gap-1.5 flex-wrap">
                    <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => window.open(`/api/SalesOrder/DispatchSlipPdf/${d.id}`, "_blank")}>
                      <FileText className="size-3.5" /> Slip
                    </Button>
                    <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => window.open(`/api/SalesOrder/DeliveryChallanPdf/${d.id}`, "_blank")}>
                      <ReceiptText className="size-3.5" /> Challan
                    </Button>
                    <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => window.open(`/api/SalesOrder/PackingListPdf/${d.id}`, "_blank")}>
                      <Package className="size-3.5" /> Packing list
                    </Button>
                  {canUpdate && d.dispatch_status === "DELIVERED" && d.lines.some((dl) => (dl.rolls || []).some((r) => r.stock_unit_id && r.stock_status === "DISPATCHED")) && (
                    <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => setReturnFor(d)}>
                      <Undo2 className="size-3.5" /> Goods returned
                    </Button>
                  )}
                  {canUpdate && d.dispatch_status === "DELIVERED" && (
                    <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => setPodFor(d)}>
                      <FileCheck2 className="size-3.5" /> {d.has_pod ? "Replace proof" : "Add proof of delivery"}
                    </Button>
                  )}
                  {canUpdate && onTheRoad(d) && (
                    <>
                      <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => setTrackFor(d)}>
                        <MapPin className="size-3.5" /> Track
                      </Button>
                      <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => setEwayFor(d)}>
                        <ReceiptText className="size-3.5" /> E-way bill
                      </Button>
                      <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => openAction({ kind: "deliver", dispatch: d })}>
                        <CheckCircle2 className="size-3.5" /> Mark delivered
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 text-xs text-red-600" onClick={() => openAction({ kind: "cancelDispatch", dispatch: d })}>
                        Cancel
                      </Button>
                    </>
                  )}
                  </div>
                </div>
                <div className="text-xs text-foreground/80">
                  {d.lines.map((dl) => {
                    const l = lineName(dl.order_line_id);
                    return (
                      <span key={dl.id} className="mr-3">
                        {l?.product_name}
                        {l?.variant_name ? ` (${l.variant_name})` : ""}: <strong>{num(dl.quantity)} {l?.unit}</strong>
                        {dl.packages ? ` · ${dl.packages} rolls` : ""}
                        {(() => {
                          const lots = [...new Set([dl.lot_number, ...(dl.rolls || []).map((r) => r.lot_number)].filter(Boolean))];
                          return lots.length ? ` · lot ${lots.join(", ")}` : "";
                        })()}
                        {dl.rolls?.length ? ` · roll list` : ""}
                      </span>
                    );
                  })}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {order.notes && (
        <div className="rounded-xl border bg-card px-4 py-3 text-sm">
          <p className="text-[11px] uppercase font-semibold tracking-wider text-muted-foreground mb-1">Notes</p>
          {order.notes}
        </div>
      )}

      <DispatchDialog order={order} open={dispatchOpen} onClose={() => setDispatchOpen(false)} onSaved={load} />
      <TrackingDialog dispatch={trackFor} onClose={() => setTrackFor(null)} onSaved={load} />
      <EwayDialog dispatch={ewayFor} onClose={() => setEwayFor(null)} onSaved={load} />
      <PodDialog dispatch={podFor} onClose={() => setPodFor(null)} onSaved={load} />
      <ReturnDialog dispatch={returnFor} open={!!returnFor} onClose={() => setReturnFor(null)} onSaved={load} />
      <StockPickerDialog
        open={!!reserveLine}
        mode="reserve"
        orderId={order.id}
        line={reserveLine ? { id: reserveLine.id, product_name: reserveLine.product_name, variant_name: reserveLine.variant_name, unit: reserveLine.unit, needed: num(reserveLine.pending_qty), max: num(reserveLine.max_dispatch_qty) } : null}
        onClose={() => setReserveLine(null)}
        onReserved={load}
      />
      <AmendLinesDialog order={order} open={amendOpen} onClose={() => setAmendOpen(false)} onSaved={load} />
      <ProformaDialog order={order} open={proformaOpen} canIssue={canUpdate && !cancelled} onClose={() => setProformaOpen(false)} onSaved={load} />
      <ComplaintFormDialog
        target={complaintOpen ? "new" : null}
        order={{ id: order.id, order_number: order.order_number, account_name: order.account?.name, dispatches: order.dispatches }}
        onClose={() => setComplaintOpen(false)}
        onSaved={load}
      />

      <Dialog open={!!action} onOpenChange={(o) => !o && !busy && setAction(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{action?.kind === "deliver" ? "Mark as delivered" : action?.kind === "cancelDispatch" ? "Cancel dispatch" : "Cancel order"}</DialogTitle>
            <DialogDescription>
              {action?.kind === "cancelDispatch" ? "The quantity goes back to pending on the order." : action?.kind === "cancelOrder" ? "Nothing has been sent, so the order can be cancelled." : action?.dispatch?.dispatch_number}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {action?.kind === "deliver" ? (
              <>
                <div className="space-y-1.5">
                  <Label>Delivered on</Label>
                  <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Received by</Label>
                  <Input value={receivedBy} onChange={(e) => setReceivedBy(e.target.value)} placeholder="Name of the person who signed" />
                </div>
                <div className="space-y-1.5">
                  <Label>Proof of delivery (signed challan, optional)</Label>
                  <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/*" onChange={(e) => setPodFile(e.target.files?.[0] || null)} className="block w-full text-xs" />
                </div>
              </>
            ) : (
              <div className="space-y-1.5">
                <Label>Reason *</Label>
                <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAction(null)} disabled={busy}>
              Back
            </Button>
            <Button
              variant={action?.kind === "deliver" ? "default" : "destructive"}
              disabled={busy || (action?.kind !== "deliver" && !text.trim())}
              onClick={() => {
                if (!action) return;
                if (action.kind === "deliver")
                  run(async () => {
                    await api.patch(`/api/SalesOrder/Delivered/${action.dispatch!.id}`, { delivered_on: date || undefined, received_by: receivedBy.trim() || undefined });
                    if (podFile) await uploadPod(action.dispatch!.id, podFile, receivedBy);
                  }, "Marked as delivered");
                else if (action.kind === "cancelDispatch") run(() => api.post(`/api/SalesOrder/CancelDispatch/${action.dispatch!.id}`, { reason: text.trim() }), "Dispatch cancelled");
                else run(() => api.post(`/api/SalesOrder/Cancel/${order.id}`, { reason: text.trim() }), "Order cancelled");
              }}
            >
              {busy ? <Loader2 className="size-4 animate-spin mr-1.5" /> : null}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={(o) => !o && !busy && setEditOpen(false)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit order details</DialogTitle>
            <DialogDescription>The lines come from the accepted quotation and are not changed here.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>PO number</Label>
                <Input value={edit.po_number} onChange={(e) => setEdit({ ...edit, po_number: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>PO date</Label>
                <Input type="date" value={edit.po_date} onChange={(e) => setEdit({ ...edit, po_date: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Delivery terms</Label>
                <select value={edit.delivery_terms} onChange={(e) => setEdit({ ...edit, delivery_terms: e.target.value })} className="w-full h-10 rounded-lg border border-input bg-background px-3 text-sm">
                  <option value="">-- Not set --</option>
                  {Object.entries(DELIVERY_TERMS).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label>Payment terms</Label>
                <Input value={edit.payment_terms} onChange={(e) => setEdit({ ...edit, payment_terms: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Textarea rows={3} value={edit.notes} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button
              disabled={busy || !edit.po_number.trim()}
              onClick={() => run(() => api.put(`/api/SalesOrder/Update/${order.id}`, { po_number: edit.po_number.trim(), po_date: edit.po_date || null, delivery_terms: edit.delivery_terms, payment_terms: edit.payment_terms, notes: edit.notes }), "Order updated")}
            >
              {busy ? <Loader2 className="size-4 animate-spin mr-1.5" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-3.5">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-sm font-medium mt-1 break-words">{value}</p>
    </div>
  );
}
